<script setup lang="ts">
import type { NavItem } from '~/types/ui/uiTypes'

const props = withDefaults(defineProps<{
  tabs: NavItem[]
  activePath: string
  showMore?: boolean
  isMoreOpen?: boolean
  isMoreActive?: boolean
}>(), {
  showMore: false,
  isMoreOpen: false,
  isMoreActive: false,
})

const emit = defineEmits<{
  toggleMore: []
}>()

const gridColsClass = computed(() => {
  const cols = props.tabs.length + (props.showMore ? 1 : 0)
  if (cols <= 2)
    return 'grid-cols-2'
  if (cols === 3)
    return 'grid-cols-3'
  return 'grid-cols-4'
})

const resolvedActiveIndicatorClass = 'bg-accent-2'
const resolvedActiveBackgroundClass = 'bg-accent-2-soft text-accent-2'
const navClasses = 'border-rule-soft bg-paper-2/85'

const moreButtonColorClasses = computed(() =>
  props.isMoreActive ? 'text-accent-2' : 'text-ink-soft',
)

const moreButtonHoverClasses = 'hover:bg-rule-soft'
</script>

<template>
  <nav
    class="fixed inset-x-0 bottom-0 z-40 h-12 rounded-t-2xl border-t shadow-lg backdrop-blur-md"
    :class="navClasses"
    style="padding-bottom: env(safe-area-inset-bottom);"
    role="navigation"
    aria-label="Bottom tabs"
  >
    <div class="grid gap-1 px-1" :class="gridColsClass">
      <UIBottomTabItem
        v-for="item in props.tabs"
        :key="item.path"
        :to="item.path"
        :label="item.label"
        :icon="item.icon"
        :active="isActivePath(activePath, item.path)"
        :prefetch="props.activePath !== '/'"
        :active-indicator-class="resolvedActiveIndicatorClass"
        :active-background-class="resolvedActiveBackgroundClass"
      />

      <button
        v-if="props.showMore"
        type="button"
        class="relative flex h-12 flex-col items-center justify-center gap-0.5 px-1 text-center transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-2 disabled:opacity-60"
        :class="moreButtonColorClasses"
        :aria-expanded="props.isMoreOpen ? 'true' : 'false'"
        aria-label="More"
        @click="emit('toggleMore')"
      >
        <span
          class="absolute top-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full transition-opacity"
          :class="props.isMoreActive ? `${resolvedActiveIndicatorClass} opacity-100` : 'opacity-0'"
          aria-hidden="true"
        />
        <div
          class="flex flex-col items-center justify-center gap-0.5 rounded-md px-2 py-1 transition-colors"
          :class="props.isMoreActive || props.isMoreOpen ? resolvedActiveBackgroundClass : moreButtonHoverClasses"
        >
          <UIIcon name="overflow-menu-horizontal" class="text-[18px] leading-none" />
          <span class="text-[10px] leading-none">More</span>
        </div>
      </button>
    </div>
  </nav>
</template>
